import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Check, Delete, Edit } from '@mui/icons-material';
import apis from '../../../apis';
import styles from '../styles';
import { useToastContext } from '../../../components/Toast/toastContext';

/** Match Manage Widgets actions: rounded icon button w/ light hover circle */
const rowActionIconButtonSx = {
  borderRadius: '50%',
  width: 36,
  height: 36,
  color: 'secondary.main',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'secondary.light'
  },
  '& svg': {
    flexShrink: 0,
    width: 24,
    height: 24
  }
} as const;

const DomainWhitelistSettings: React.FC = () => {
  const { activeToast } = useToastContext();
  const activeToastRef = useRef(activeToast);
  const [domains, setDomains] = useState<string[]>(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const editableInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    activeToastRef.current = activeToast;
  }, [activeToast]);

  const isValidDomainUrl = (domain: string) => {
    const value = domain.trim();
    return value.length > 0 && /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(value);
  };

  useEffect(() => {
    setLoading(true);
    apis.settings
      .getDomainWhitelist()
      .then((res) => {
        if (res.success) {
          const list = res.domains?.length ? res.domains : res.editableDomains?.length ? res.editableDomains : [''];
          setDomains(list.length ? list : ['']);
        } else {
          activeToastRef.current({ type: 'error', text: 'Failed to load domain whitelist.' });
        }
      })
      .catch((err) => {
        activeToastRef.current({ type: 'error', text: err.msg || 'Failed to load domain whitelist.' });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDomainChange = (index: number, value: string) => {
    setDomains((prev) => prev.map((domain, i) => (i === index ? value : domain)));
  };

  const addDomainField = () => {
    setDomains((prev) => [...prev, '']);
  };

  const removeDomainField = async (index: number) => {
    if (saving) return;

    const prevDomains = domains;
    const nextDomains = domains.filter((_, i) => i !== index);
    const normalizedNext = nextDomains.length > 0 ? nextDomains : [''];

    const invalidRemaining = normalizedNext.some((d) => {
      const v = d.trim();
      return v.length > 0 && !isValidDomainUrl(v);
    });

    if (invalidRemaining) {
      activeToast({ type: 'error', text: 'Fix invalid URLs before removing.' });
      return;
    }

    const cleaned = Array.from(
      new Set(normalizedNext.map((d) => d.trim()).filter((d) => d.length > 0 && isValidDomainUrl(d)))
    );

    setDomains(normalizedNext);
    setEditingIndex(null);
    setSaving(true);

    try {
      const res = await apis.settings.updateDomainWhitelist(cleaned);
      if (res.success) {
        const list = res.domains?.length ? res.domains : res.editableDomains || [];
        setDomains(list.length ? list : ['']);
        activeToast({ type: 'success', text: 'Domain removed!' });
      } else {
        setDomains(prevDomains);
        activeToast({ type: 'error', text: 'Failed to remove domain.' });
      }
    } catch (err: any) {
      setDomains(prevDomains);
      activeToast({ type: 'error', text: err?.msg || 'Failed to remove domain.' });
    } finally {
      setSaving(false);
      setEditingIndex(null);
    }
  };

  const focusEditableInput = (index: number) => {
    editableInputRefs.current[index]?.focus();
  };

  const saveDomains = async (nextDomains: string[]) => {
    const invalid = nextDomains.some((d) => {
      const v = d.trim();
      return v.length > 0 && !isValidDomainUrl(v);
    });

    if (invalid) {
      activeToast({ type: 'error', text: 'Please fix invalid URLs before saving.' });
      return;
    }

    const cleaned = Array.from(new Set(nextDomains.map((d) => d.trim()).filter((d) => isValidDomainUrl(d))));

    setSaving(true);
    try {
      const res = await apis.settings.updateDomainWhitelist(cleaned);
      if (res.success) {
        const list = res.domains?.length ? res.domains : res.editableDomains || [];
        setDomains(list.length ? list : ['']);
        activeToast({ type: 'success', text: 'Domain whitelist saved!' });
      } else {
        activeToast({ type: 'error', text: 'Failed to save domain whitelist.' });
      }
    } catch (err: any) {
      activeToast({ type: 'error', text: err?.msg || 'Failed to save domain whitelist.' });
    } finally {
      setSaving(false);
      setEditingIndex(null);
    }
  };

  const handleConfirmEdit = async (index: number) => {
    const current = domains[index] ?? '';
    if (!isValidDomainUrl(current)) {
      activeToast({ type: 'error', text: 'Enter a valid URL starting with http:// or https://.' });
      return;
    }
    await saveDomains(domains);
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={styles.title}>
        Domain Whitelist
      </Typography>
      <Typography sx={styles.description}>
        Domains where widgets are allowed. Edit or remove any entry, or add new ones. Use Edit + Check (or Enter) to
        save changes.
      </Typography>

      <Table sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.th}>Domain URL</TableCell>
            <TableCell
              align="right"
              sx={(theme) => ({
                ...styles.th(theme),
                width: 140,
                whiteSpace: 'nowrap',
                textAlign: 'right'
              })}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {domains.map((domain, index) => (
            <TableRow key={`domain-${index}`} sx={styles.tr}>
              <TableCell sx={styles.td}>
                <TextField
                  fullWidth
                  size="small"
                  value={domain}
                  onChange={(event) => handleDomainChange(index, event.target.value)}
                  placeholder="https://example.com"
                  disabled={saving}
                  onFocus={() => setEditingIndex(index)}
                  onBlur={() => {
                    setTimeout(() => {
                      setEditingIndex((prev) => (prev === index ? null : prev));
                    }, 0);
                  }}
                  inputRef={(el) => {
                    editableInputRefs.current[index] = el;
                  }}
                  error={domain.trim().length > 0 && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(domain.trim())}
                  helperText={
                    domain.trim().length > 0 && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(domain.trim())
                      ? 'Enter a valid URL with http:// or https://'
                      : ''
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editingIndex === index) {
                      e.preventDefault();
                      handleConfirmEdit(index);
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={styles.td} align="right">
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={4}>
                  {editingIndex === index ? (
                    <Tooltip title="Save">
                      <IconButton
                        size="medium"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleConfirmEdit(index)}
                        disabled={saving || !isValidDomainUrl(domain)}
                        aria-label="confirm domain"
                        sx={rowActionIconButtonSx}
                      >
                        <Check />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Edit">
                      <IconButton
                        size="medium"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setEditingIndex(index);
                          focusEditableInput(index);
                        }}
                        disabled={saving}
                        aria-label="edit domain"
                        sx={rowActionIconButtonSx}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Remove">
                    <IconButton
                      size="medium"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void removeDomainField(index)}
                      disabled={saving}
                      aria-label="remove domain"
                      sx={rowActionIconButtonSx}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center" sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<Add />} onClick={addDomainField} disabled={saving}>
          Add domain
        </Button>
      </Stack>
    </Box>
  );
};

export default DomainWhitelistSettings;
